import { useCallback, useMemo, useState } from "react";

/**
 * Keyboard-driven selection for a vertical list/table. Owns the selected item id
 * and translates arrow / Home / End / Enter keys into selection moves and an
 * "activate" action (e.g. open a detail view). Kept UI-agnostic so any list can
 * reuse it — the consumer decides how a selected row looks and where the key
 * handler is attached (usually a focusable scroll container).
 */
export interface KeyboardListSelection {
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  /** Attach to a focusable container (tabIndex={0}); drives the whole interaction. */
  handleKeyDown: (event: React.KeyboardEvent) => void;
}

interface Options<T> {
  items: T[];
  getId: (item: T) => string;
  /** Invoked when the user presses Enter on the selected item. */
  onActivate?: (item: T) => void;
  enabled?: boolean;
}

export function useKeyboardListSelection<T>({
  items,
  getId,
  onActivate,
  enabled = true,
}: Options<T>): KeyboardListSelection {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const ids = useMemo(() => items.map(getId), [items, getId]);

  const moveTo = useCallback(
    (index: number) => {
      if (ids.length === 0) return;
      const clamped = Math.max(0, Math.min(ids.length - 1, index));
      setSelectedId(ids[clamped]);
    },
    [ids],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (!enabled || ids.length === 0) return;
      const current = selectedId ? ids.indexOf(selectedId) : -1;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          moveTo(current + 1);
          break;
        case "ArrowUp":
          event.preventDefault();
          moveTo(current - 1);
          break;
        case "Home":
          event.preventDefault();
          moveTo(0);
          break;
        case "End":
          event.preventDefault();
          moveTo(ids.length - 1);
          break;
        case "Enter":
          if (current >= 0 && onActivate) {
            event.preventDefault();
            onActivate(items[current]);
          }
          break;
        default:
          break;
      }
    },
    [enabled, ids, selectedId, moveTo, onActivate, items],
  );

  return { selectedId, setSelectedId, handleKeyDown };
}
